import { useMemo } from 'react';
import { SWRConfiguration } from 'swr';

import {
  RelationshipType,
  useGetManagerEmployeeRelationshipType,
} from '~/data/search';
import { Roles } from '~/data/user';
import { useSWR } from '~/hooks/useSWR';
import { fetchFromOldApi } from '~/services/Http';

const SWR_KEY_GET_ROLE_RESPONSIBILITY_OVER_EMPLOYEE =
  'SWR_KEY_GET_ROLE_RESPONSIBILITY_OVER_EMPLOYEE';

export const getRoleResponsibilityOverEmployee = (
  role: string,
  responsibleEmployeeId: number,
  employeeId: number,
): Promise<boolean> => {
  return fetchFromOldApi(
    `/json/auth/role/${role}/${responsibleEmployeeId}/emp/${employeeId}`,
  ).then((r) => r.json());
};

type PropsType = {
  roles: string[];
  responsibleEmployeeId?: number;
  employeeId?: number;
};

type useGetRoleResponsibilityOverEmployeeOutput = {
  responsibilityRoles: Roles[];
  managerRelationshipType: RelationshipType;
  isLoading: boolean;
  isValidating: boolean;
  mutate: () => void;
};

export const useGetRoleResponsibilityOverEmployee = (
  props: PropsType,
  swrConfig?: SWRConfiguration<Roles[]>,
): useGetRoleResponsibilityOverEmployeeOutput => {
  const { roles, responsibleEmployeeId, employeeId } = props;

  const defaultConfig = {
    revalidateOnFocus: false,
    refreshInterval: 0,
  };

  const { checkRoleResponsibilitiesFromAuth, checkManagerRelationship } =
    useMemo(() => {
      const allSupervisorRoles = [
        Roles.HR_DEV,
        Roles.HR_ADMIN,
        Roles.HR_ADMIN_LIMITED,
        Roles.HR_STAFF,
        Roles.HR_SALARY,
        Roles.HR_AUDITOR,
        Roles.HR_PEOP,
        Roles.HR_PEOP_DEV,
        Roles.DEPUTY_MANAGER,
        Roles.ALT_MANAGER,
        Roles.MANAGER,
        Roles.SUBSTITUTE_ABSENCE_MANAGER,
      ].map((r) => r.toString());

      const validRoles = roles.filter((r) => allSupervisorRoles.includes(r));

      return {
        checkRoleResponsibilitiesFromAuth: validRoles.filter(
          (r) => r !== Roles.MANAGER,
        ),
        checkManagerRelationship: validRoles.includes(Roles.MANAGER),
      };
    }, [roles]);

  // Check Manager responsibility from OrgTree
  const { data: relationship, ...relationshipRest } =
    useGetManagerEmployeeRelationshipType(
      checkManagerRelationship
        ? {
            employeeId: responsibleEmployeeId,
            targetId: employeeId,
          }
        : {},
      { ...defaultConfig, ...(swrConfig as SWRConfiguration) },
    );

  // Check other roles responsibility from Auth
  const { data, ...responsibilityRest } = useSWR(
    !!responsibleEmployeeId &&
      !!employeeId &&
      !!checkRoleResponsibilitiesFromAuth &&
      checkRoleResponsibilitiesFromAuth.length > 0
      ? [
          SWR_KEY_GET_ROLE_RESPONSIBILITY_OVER_EMPLOYEE,
          responsibleEmployeeId,
          checkRoleResponsibilitiesFromAuth,
          employeeId,
        ]
      : null,
    async ([key_, responsibleEmployeeId, roles, employeeId]) => {
      return Promise.all(
        (roles as string[]).map((role) =>
          getRoleResponsibilityOverEmployee(
            role,
            responsibleEmployeeId,
            employeeId,
          ).then((result) => (result ? role : null)),
        ),
      ).then((results): Roles[] =>
        results.reduce((acc, role) => {
          return role !== null ? [...acc, role as Roles] : acc;
        }, [] as Roles[]),
      );
    },
    { ...defaultConfig, ...swrConfig },
  );

  // Resolve results
  const { responsibilityRoles, managerRelationshipType } = useMemo(() => {
    const extendWithITAdmin = (
      myRoles: string[],
      responsibleRoles: Roles[],
    ) => {
      return myRoles.includes(Roles.IT_ADMIN)
        ? [...responsibleRoles, Roles.IT_ADMIN]
        : responsibleRoles;
    };

    const authVerifiedRoles = extendWithITAdmin(roles, data ?? []);

    if (checkManagerRelationship && !!relationship) {
      return {
        responsibilityRoles: [
          RelationshipType.Direct,
          RelationshipType.Indirect,
        ].includes(relationship)
          ? [...authVerifiedRoles, Roles.MANAGER]
          : authVerifiedRoles,
        managerRelationshipType:
          checkManagerRelationship && !!relationship
            ? relationship
            : RelationshipType.None,
      };
    }

    return {
      responsibilityRoles: authVerifiedRoles,
      managerRelationshipType: RelationshipType.None,
    };
  }, [roles, data, relationship, checkManagerRelationship]);

  return {
    responsibilityRoles,
    managerRelationshipType,
    isLoading: responsibilityRest.isLoading || relationshipRest.isLoading,
    isValidating:
      responsibilityRest.isValidating || relationshipRest.isValidating,
    mutate: () => {
      responsibilityRest.mutate();
      relationshipRest.mutate();
    },
  };
};
